import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { Grid, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { ErrorOption, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { FormInput } from 'components/molecules/FormInput';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import FormLabel from 'components/atoms/FormLabel';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import {
  EgoodReasonValues,
  EsafeStorageValues,
  IDocumentInformation,
  ILicenceTemplateFormValues,
  IOwnerIndividual,
  IOwnerOrganisation,
} from 'models/licenceConfigurator';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { useScroll } from 'hooks/useScroll';
import { IDictionary } from 'models/dictionary';
import { FormNumberInput } from 'components/molecules/FormNumberInput';
import * as yup from 'yup';
import { FormListWrapper } from 'components/atoms/FormListWrapper';
import { colors } from 'theme/colors';
import { useWatch } from 'react-hook-form';
import { useFormSchema } from '../schemaValidation';
import { StyledLink, StyledNameSection } from '../styles';
import { useConfigurationLicence } from '../hooks';
import {
  documentLegalEntityIndividualDataFields,
  documentLegalEntityOrganisationDataFields,
  ELicenceCardInformationFormLabels,
  ELicenceFormLabels,
  individualRequired,
  licenceDataFields,
  organisationRequired,
  ownerLegalEntityIndividualDataFields,
  ownerLegalEntityIndividualDisable,
  ownerLegalEntityOrganisationDataFields,
  ownerLegalEntityOrganisationDisable,
  weaponDataFields,
} from '../helper';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

interface IFormLicenceTemplatePage {
  initialFormValues: ILicenceTemplateFormValues;
  isEdit?: boolean;
  version?: string | null;
}

const FormLicenceTemplatePage = ({
  initialFormValues,
  isEdit = false,
  version = null,
}: IFormLicenceTemplatePage) => {
  const navigate = useNavigate();
  const { schema } = useFormSchema();
  const [isLoading, setIsLoading] = useState(false);
  const { elRef, scrollToError } = useScroll();
  const { weaponLicenceGoodReason } = useConfigurationLicence();
  const { getFieldsOptional, hiddenFieldsName, requiredFieldsName } =
    useSystemFieldsConfiguration();
  const { t } = useTranslations();
  const { id } = useParams<{ id: string }>();
  const [isGoodReasonDictionary, setIsGoodReasonDictionary] = useState<boolean>(false);

  const [individualUserType, setIndividualUserType] = useState<boolean>(
    initialFormValues.owner.legalTypes?.INDIVIDUAL ?? false,
  );
  const [individualInitialLoad, setIndividualInitialLoad] = useState<boolean>(
    initialFormValues.owner.legalTypes?.INDIVIDUAL ?? false,
  );

  const [organizationUserType, setOrganizationUserType] = useState<boolean>(
    initialFormValues.owner.legalTypes?.ORGANIZATION ?? false,
  );
  const [organisationInitialLoad, setOrganisationInitialLoad] = useState<boolean>(
    initialFormValues.owner.legalTypes?.ORGANIZATION ?? false,
  );

  const [weaponDataChecked, setWeaponDataChecked] = useState<boolean>(false);
  const [licenceDataChecked, setLicenceDataChecked] = useState<boolean>(false);

  const [ownerLegalEntityOrganisationChecked, setOwnerLegalEntityOrganisationChecked] =
    useState<boolean>(false);
  const [ownerLegalEntityIndividualChecked, setOwnerLegalEntityIndividualChecked] =
    useState<boolean>(false);

  const [legalEntityOrganisationDataChecked, setLegalEntityOrganisationDataChecked] =
    useState<boolean>(false);
  const [legalEntityIndividualDataChecked, setLegalEntityIndividualDataChecked] =
    useState<boolean>(false);

  const [goodReasonDictionaryChecked, setGoodReasonDictionaryChecked] =
    useState<boolean>(false);

  const [enabledLegalEntityIndividual, setEnabledLegalEntityIndividual] = useState<string[]>(
    [],
  );
  const [enabledLegalEntityOrganisation, setEnabledLegalEntityOrganisation] = useState<
    string[]
  >([]);

  const [errorLegalType, setErrorLegalType] = useState<ErrorOption | null>();
  const { addLicenceTemplate, editLicenceTemplate } = useConfigurationLicence();

  const extendedSchema = useMemo(() => {
    const ownerSchema = yup.reach(schema, 'owner') as yup.ObjectSchema<any>;

    const extendedOwner = ownerSchema.shape({
      individual: yup.object({
        ageLimit: individualUserType
          ? yup
              .number()
              .typeError(t('general.field.validation.required'))
              .test(
                'required',
                t('general.field.validation.valueIsNotCorrect'),
                (value) => value !== undefined && value !== null && value > 0,
              )
              .max(100, t('general.field.validation.valueIsNotCorrect'))
              .nullable()
          : yup.string().nullable(),
      }),
      roleIds: yup.array().of(yup.string()),
    });

    return schema.shape({
      owner: extendedOwner,
    });
  }, [individualUserType, schema, t]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ILicenceTemplateFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(extendedSchema) as unknown as Resolver<ILicenceTemplateFormValues>,
  });

  useEffect(() => {
    getFieldsOptional();
  }, []);

  const watchLicenceDurationUnlimited = watch('general.licenceDurationUnlimited');
  const watchgoodReason = watch('conditions.goodReason');
  const watchSafeStorage = watch('conditions.safeStorage');
  const watchSpecificCountryArea = watch('conditions.specificCountryArea');
  const watchCompetency = watch('conditions.competency');
  const watchWeaponCategory = watch('conditions.weaponCategory');
  const watchWeaponNumberLimit = watch('conditions.weaponNumberLimit');
  const watchCarryConditions = watch('conditions.carryConditions');
  const watchAdditionalConditions = watch('conditions.additionalConditions');

  const organisationValues = useWatch({
    control,
    name: 'owner.organisation',
  });
  const allCheckedOrganisation =
    organisationValues && Object.values(organisationValues).every(Boolean);

  const individualValues = useWatch({
    control,
    name: 'owner.individual',
  });
  const allCheckedIndividual =
    individualValues &&
    Object.values(individualValues)
      .filter((v) => typeof v === 'boolean')
      .every(Boolean);

  const GoodReasonValues = useWatch({
    control,
    name: 'conditions.goodReasonDictionaryIds',
  });
  const allCheckedGoodReasonDictionaryIds =
    GoodReasonValues && Object.values(GoodReasonValues).every(Boolean);

  const getValueName = (item: string) => {
    switch (item) {
      case 'legalEntityIndividualId':
      case 'legalEntityOrganisationId':
        return 'legalEntityId';
      case 'individualPrimaryAddress':
      case 'organisationPrimaryAddress':
        return 'primaryAddress';
      default:
        return item;
    }
  };

  // const checkDocumentLegalEntity = (item: string, type: ELegalEntityTypes): boolean => {
  //   const name = getValueName(item);
  //   if (type === ELegalEntityTypes.individual) {
  //     return (
  //       !(ownerLegalEntityIndividualDisable.includes(name) && individualUserType) &&
  //       !enabledLegalEntityIndividual.includes(name)
  //     );
  //   }
  //   if (type === ELegalEntityTypes.organisation) {
  //     return (
  //       !(ownerLegalEntityOrganisationDisable.includes(name) && organizationUserType) &&
  //       !enabledLegalEntityOrganisation.includes(name)
  //     );
  //   }
  //   return false;
  // };

  const checkboxIsDisable = (item: string) => {
    switch (item) {
      case 'region':
        return !watchSpecificCountryArea;
      case 'competency':
        return !watchCompetency;
      case 'weaponCategory':
        return !watchWeaponCategory;
      case 'weaponNumberLimit':
        return !watchWeaponNumberLimit;
      case 'carryConditions':
        return !watchCarryConditions;
      case 'additionalConditions':
        return !watchAdditionalConditions;
      case 'goodReason':
        return watchgoodReason === 'false';
      case 'safeStorage':
        return watchSafeStorage === 'false';
      case 'address':
        return !individualUserType && !organizationUserType;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (!watchSpecificCountryArea) {
      setValue('documentInformation.region', false);
    }
  }, [watchSpecificCountryArea]);
  useEffect(() => {
    if (!watchCompetency) {
      setValue('documentInformation.competency', false);
    }
  }, [watchCompetency]);
  useEffect(() => {
    if (!watchWeaponCategory) {
      setValue('documentInformation.weaponCategory', false);
    }
  }, [watchWeaponCategory]);
  useEffect(() => {
    if (!watchWeaponNumberLimit) {
      setValue('documentInformation.weaponNumberLimit', false);
    }
  }, [watchWeaponNumberLimit]);
  useEffect(() => {
    if (!watchCarryConditions) {
      setValue('documentInformation.carryConditions', false);
    }
  }, [watchCarryConditions]);
  useEffect(() => {
    if (!watchAdditionalConditions) {
      setValue('documentInformation.additionalConditions', false);
    }
  }, [watchAdditionalConditions]);
  useEffect(() => {
    if (watchgoodReason === 'false') {
      setValue('documentInformation.goodReason', false);
    }
  }, [watchgoodReason]);

  useEffect(() => {
    if (isEdit && initialFormValues?.conditions?.goodReason === null) {
      setValue('conditions.goodReason', 'false');
    }
    if (isEdit && initialFormValues?.conditions?.safeStorage === null) {
      setValue('conditions.safeStorage', 'false');
    }
    if (isEdit) {
      setEnabledLegalEntityIndividual(
        Object.entries(initialFormValues.owner.individual)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(
            ([key, value]) =>
              value === true || requiredFieldsName.includes(key as EFieldsOptional),
          )
          .map(([key]) => key),
      );
      setEnabledLegalEntityOrganisation(
        Object.entries(initialFormValues.owner.organisation)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(
            ([key, value]) =>
              value === true || requiredFieldsName.includes(key as EFieldsOptional),
          )
          .map(([key]) => key),
      );
    }
  }, [initialFormValues]);

  useEffect(() => {
    if (!individualUserType) {
      individualRequired.map((value: string) =>
        setValue(
          `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
          false,
        ),
      );
    }
    if (!organizationUserType) {
      organisationRequired.map((value: string) =>
        setValue(
          `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
          false,
        ),
      );
    }
  }, [individualUserType, organizationUserType]);

  const disabledGoodReasonDictionary = () => {
    if (weaponLicenceGoodReason) {
      weaponLicenceGoodReason.map((item: IDictionary) =>
        setValue(`conditions.goodReasonDictionaryIds.${item.uniqueId.toString()}`, false),
      );
      setGoodReasonDictionaryChecked(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (errors) {
        const errorList: any = [];
        const keys = Object.keys(errors);
        keys.map((key) => {
          const items = (errors as Record<string, any[]>)[key];
          // eslint-disable-next-line no-return-assign
          Object.keys(items)?.forEach((k: any) => (errorList[`${key}.${k}`] = items[k]));
          return null;
        });
        scrollToError(errorList);
      }
    })();
  }, [errors]);

  const enableLegalEntityIndividualData = (
    val: boolean,
    itemName: string | null,
    itemNames?: string[],
  ) => {
    if (val) {
      if (itemName) {
        setEnabledLegalEntityIndividual([...enabledLegalEntityIndividual, itemName]);
      } else if (itemNames) {
        setEnabledLegalEntityIndividual(itemNames);
      }
    } else if (!val) {
      if (itemName) {
        setEnabledLegalEntityIndividual(
          enabledLegalEntityIndividual.filter((item: string) => item !== itemName),
        );
        setValue(
          `documentInformation.${itemName}` as `documentInformation.${keyof IDocumentInformation}`,
          false,
        );
      } else if (itemNames) {
        setEnabledLegalEntityIndividual([]);
        // eslint-disable-next-line array-callback-return
        itemNames?.map((name: string) => {
          setValue(
            `documentInformation.${name}` as `documentInformation.${keyof IDocumentInformation}`,
            false,
          );
        });
      }
    }
  };
  const enableLegalEntityOrganisationData = (
    val: boolean,
    itemName: string | null,
    itemNames?: string[],
  ) => {
    if (val) {
      if (itemName) {
        setEnabledLegalEntityOrganisation([...enabledLegalEntityOrganisation, itemName]);
      } else if (itemNames) {
        setEnabledLegalEntityOrganisation(itemNames);
      }
    } else if (!val) {
      if (itemName) {
        setEnabledLegalEntityOrganisation(
          enabledLegalEntityOrganisation.filter((item: string) => item !== itemName),
        );
        setValue(
          `documentInformation.${itemName}` as `documentInformation.${keyof IDocumentInformation}`,
          false,
        );
      } else if (itemNames) {
        setEnabledLegalEntityOrganisation([]);
        // eslint-disable-next-line array-callback-return
        itemNames?.map((name: string) => {
          setValue(
            `documentInformation.${name}` as `documentInformation.${keyof IDocumentInformation}`,
            false,
          );
        });
      }
    }
  };

  const checkAll = (section: string, clear: boolean = false) => {
    if (section === 'weaponData') {
      // eslint-disable-next-line array-callback-return
      weaponDataFields.map((value: string) => {
        if (!checkboxIsDisable(value)) {
          setValue(
            `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
            !weaponDataChecked,
          );
        }
      });
      setWeaponDataChecked(!weaponDataChecked);
    }
    if (section === 'legalEntityOrganisationData') {
      // eslint-disable-next-line array-callback-return
      documentLegalEntityOrganisationDataFields
        .filter((val: string) => !hiddenFieldsName.includes(val as EFieldsOptional))
        .map((value: string) => {
          if (clear) {
            setValue(
              `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
              false,
            );
          } else if (
            ((ownerLegalEntityOrganisationDisable.includes(getValueName(value)) ||
              requiredFieldsName.includes(value as EFieldsOptional)) &&
              organizationUserType) ||
            enabledLegalEntityOrganisation.includes(getValueName(value))
          ) {
            setValue(
              `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
              !legalEntityOrganisationDataChecked,
            );
          }
        });
      setLegalEntityOrganisationDataChecked(
        clear ? false : !legalEntityOrganisationDataChecked,
      );
    }
    if (section === 'legalEntityIndividualData') {
      // eslint-disable-next-line array-callback-return
      documentLegalEntityIndividualDataFields
        .filter((val: string) => !hiddenFieldsName.includes(val as EFieldsOptional))
        .map((value: string) => {
          if (clear) {
            setValue(
              `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
              false,
            );
          } else if (
            ((ownerLegalEntityIndividualDisable.includes(getValueName(value)) ||
              requiredFieldsName.includes(value as EFieldsOptional)) &&
              individualUserType) ||
            enabledLegalEntityIndividual.includes(getValueName(value))
          ) {
            setValue(
              `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
              !legalEntityIndividualDataChecked,
            );
          }
        });
      setLegalEntityIndividualDataChecked(clear ? false : !legalEntityIndividualDataChecked);
    }
    if (section === 'licenceData') {
      // eslint-disable-next-line array-callback-return
      licenceDataFields.map((value: string) => {
        if (!checkboxIsDisable(value)) {
          setValue(
            `documentInformation.${value}` as `documentInformation.${keyof IDocumentInformation}`,
            !licenceDataChecked,
          );
        }
      });
      setLicenceDataChecked(!licenceDataChecked);
    }
    if (section === 'goodReasonDictionary') {
      if (weaponLicenceGoodReason) {
        weaponLicenceGoodReason.map((item: IDictionary) =>
          setValue(
            `conditions.goodReasonDictionaryIds.${item.uniqueId.toString()}`,
            !goodReasonDictionaryChecked,
          ),
        );
        setGoodReasonDictionaryChecked(!goodReasonDictionaryChecked);
      }
    }
    if (section === 'ownerLegalEntityOrganisation') {
      const obj = Object.fromEntries(
        Object.entries(getValues('owner.organisation')).filter(
          ([key]) => !ownerLegalEntityOrganisationDisable.includes(key),
        ),
      );
      const values = Object.values(obj);
      const allEqual = values.every((v) => v === values[0]);
      const val = allEqual ? !values[0] : !ownerLegalEntityOrganisationChecked;
      const toEnable: string[] = [];
      // eslint-disable-next-line array-callback-return
      ownerLegalEntityOrganisationDataFields
        .filter((val: string) => !hiddenFieldsName.includes(val as EFieldsOptional))
        .map((value: string) => {
          if (
            clear &&
            !ownerLegalEntityOrganisationDisable.includes(value) &&
            !requiredFieldsName.includes(value as EFieldsOptional)
          ) {
            setValue(
              `owner.organisation.${value}` as `owner.organisation.${keyof IOwnerOrganisation}`,
              false,
            );
          } else if (
            organizationUserType &&
            !ownerLegalEntityOrganisationDisable.includes(value) &&
            !requiredFieldsName.includes(value as EFieldsOptional)
          ) {
            setValue(
              `owner.organisation.${value}` as `owner.organisation.${keyof IOwnerOrganisation}`,
              val,
            );
            toEnable.push(value);
          } else if (
            ownerLegalEntityOrganisationDisable.includes(value) ||
            requiredFieldsName.includes(value as EFieldsOptional)
          ) {
            setValue(
              `owner.organisation.${value}` as `owner.organisation.${keyof IOwnerOrganisation}`,
              organizationUserType,
            );
          }
        });
      if (clear) {
        setOwnerLegalEntityOrganisationChecked(false);
        setEnabledLegalEntityOrganisation([]);
      } else {
        enableLegalEntityOrganisationData(val, null, toEnable);
        setOwnerLegalEntityOrganisationChecked(
          allEqual ? !values[0] : !ownerLegalEntityOrganisationChecked,
        );
      }
    }
    if (section === 'ownerLegalEntityIndividual') {
      const obj = Object.fromEntries(
        Object.entries(getValues('owner.individual'))
          .filter(([key]) => !ownerLegalEntityIndividualDisable.includes(key))
          .filter(([key]) => key !== 'ageLimit'),
      );
      const values = Object.values(obj);
      const allEqual = values.every((v) => v === values[0]);
      const val = allEqual ? !values[0] : !ownerLegalEntityIndividualChecked;
      const toEnable: string[] = [];
      // eslint-disable-next-line array-callback-return
      ownerLegalEntityIndividualDataFields
        .filter((val: string) => !hiddenFieldsName.includes(val as EFieldsOptional))
        .map((value: string) => {
          if (
            clear &&
            !ownerLegalEntityIndividualDisable.includes(value) &&
            !requiredFieldsName.includes(value as EFieldsOptional)
          ) {
            setValue(
              `owner.individual.${value}` as `owner.individual.${keyof IOwnerIndividual}`,
              false,
            );
          } else if (
            individualUserType &&
            !ownerLegalEntityIndividualDisable.includes(value) &&
            !requiredFieldsName.includes(value as EFieldsOptional)
          ) {
            setValue(
              `owner.individual.${value}` as `owner.individual.${keyof IOwnerIndividual}`,
              val,
            );
            if (documentLegalEntityIndividualDataFields.includes(value)) toEnable.push(value);
          } else if (
            ownerLegalEntityIndividualDisable.includes(value) ||
            requiredFieldsName.includes(value as EFieldsOptional)
          ) {
            setValue(
              `owner.individual.${value}` as `owner.individual.${keyof IOwnerIndividual}`,
              individualUserType,
            );
          }
        });
      if (clear) {
        setOwnerLegalEntityIndividualChecked(false);
        setEnabledLegalEntityIndividual([]);
      } else {
        enableLegalEntityIndividualData(val, null, toEnable);
        setOwnerLegalEntityIndividualChecked(
          allEqual ? !values[0] : !ownerLegalEntityIndividualChecked,
        );
      }
    }
  };

  const conditionChangeValue = (target: any) => {
    const value = target.value ?? '';
    const name = target.name ?? '';
    if (name === 'conditions.safeStorage' || name === 'conditions.goodReason') {
      setValue(name, value);
    } else {
      setValue(name, value === 'true');
    }

    if (name === 'general.licenceDurationUnlimited' && value === 'true') {
      setValue('general.licenceDurationYears', 0);
      setValue('general.licenceDurationMonths', 0);
      setValue('general.licenceDurationDays', 0);
    }
  };

  useEffect(() => {
    if (individualUserType !== null) {
      if (individualInitialLoad) {
        setIndividualInitialLoad(false);
        return;
      }
      checkAll('ownerLegalEntityIndividual', true);
      checkAll('legalEntityIndividualData', true);
    }
  }, [individualUserType]);

  useEffect(() => {
    if (organizationUserType !== null) {
      if (organisationInitialLoad) {
        setOrganisationInitialLoad(false);
        return;
      }
      checkAll('ownerLegalEntityOrganisation', true);
      checkAll('legalEntityOrganisationData', true);
    }
  }, [organizationUserType]);

  const onSubmitForm = async (data: ILicenceTemplateFormValues) => {
    setIsLoading(true);
    let res = null;
    if (isEdit) {
      if (id && version !== null) {
        res = await editLicenceTemplate(id, version, data);
      }
    } else {
      res = await addLicenceTemplate(data);
    }
    if (res) {
      if (isEdit && id) {
        navigate(ERouteLinks.LicenceTemplatePage.replace(':id', id));
      } else {
        navigate(ERouteLinks.LicenceConfigurationPage);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (errors.owner?.legalTypes) {
      const err = errors.owner?.legalTypes;
      if (err.message) {
        setErrorLegalType(err);
      } else if (err.root) {
        setErrorLegalType(err.root);
      }
    } else {
      setErrorLegalType(null);
    }
  }, [errors]);

  useEffect(() => {
    if (watchgoodReason === EgoodReasonValues.Dictionary) {
      setIsGoodReasonDictionary(true);
    } else {
      setIsGoodReasonDictionary(false);
    }
  }, [watchgoodReason]);

  const clearInput = (name: any) => {
    setValue(name, '');
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div ref={elRef}>
        <Grid container size={{ xs: 12 }}>
          <StyledNameSection>
            {t('licenceConfiguration.add.generalSettings.label')}
          </StyledNameSection>
        </Grid>
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="general.name"
              label={t(ELicenceFormLabels.name)}
              required={true}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <FormInput
              name="general.code"
              label={`${t(ELicenceFormLabels.code)}*`}
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormWyswigEditor
              name="general.description"
              label={t(ELicenceFormLabels.description)}
              setValue={setValue}
              control={control}
              errors={errors}
              setError={setError}
              clearErrors={clearErrors}
              charsLimit={1000}
              withValidation
              required
              showCounter
            />
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }}>
          <StyledNameSection>{t('licence.licenceParameters.label')}</StyledNameSection>
        </Grid>
        <Grid container size={{ xs: 12 }}>
          <Grid container size={{ xs: 12 }}>
            <Grid size={{ xs: 12 }}>
              <FormRadioGroup
                name="general.licenceDurationUnlimited"
                label={t(ELicenceFormLabels.licenceDuration)}
                options={[
                  { label: t(ELicenceFormLabels.licenceDurationUnlimited), value: 'true' },
                  { label: t(ELicenceFormLabels.licenceDurationLimited), value: 'false' },
                ]}
                control={control}
                errors={errors}
                onChangeOption={conditionChangeValue}
                booleanType
                required
              />
            </Grid>
          </Grid>
        </Grid>
        {!watchLicenceDurationUnlimited && (
          <Grid container size={{ xs: 12 }} sx={{ marginTop: '20px' }}>
            <Grid container size={{ xs: 12 }} columnSpacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormNumberInput
                  name="general.licenceDurationYears"
                  label={`${t(ELicenceFormLabels.licenceDurationYears)}*`}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormNumberInput
                  name="general.licenceDurationMonths"
                  label={`${t(ELicenceFormLabels.licenceDurationMonths)}*`}
                  max={11}
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormNumberInput
                  name="general.licenceDurationDays"
                  label={`${t(ELicenceFormLabels.licenceDurationDays)}*`}
                  max={30}
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid container size={{ xs: 12 }}>
          <StyledNameSection>
            {t('licenceConfiguration.add.licenceOwner.label')}
          </StyledNameSection>
        </Grid>
        <Grid sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: colors.gray900 }}>
            {t('licenceConfiguration.add.licenceOwner.description')}
            <br />
            <strong>{t('licenceConfiguration.add.licenceOwner.note.label')}</strong>{' '}
            {t('licenceConfiguration.add.licenceOwner.note.text')}
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
            <FormLabel
              name="owner.legalTypes"
              label={`${t(ELicenceFormLabels.relatedLegalEntityType)}*`}
            />
            <Grid container size={{ xs: 12 }} columnSpacing={3} sx={{ rowGap: 1 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormCheckbox
                  name="owner.legalTypes.ORGANIZATION"
                  label={t('legalEntity.organisation.label')}
                  control={control}
                  errors={errors}
                  withValidation={false}
                  onChange={(event) => {
                    setOrganizationUserType(event);
                  }}
                />
                {errorLegalType && errorLegalType.message && (
                  <FormErrorLabel label={errorLegalType.message} />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormCheckbox
                  name="owner.legalTypes.INDIVIDUAL"
                  label={t('legalEntity.individual.label')}
                  control={control}
                  errors={errors}
                  withValidation={false}
                  onChange={(event) => {
                    setIndividualUserType(event);
                    if (!event) {
                      clearInput('owner.individual.ageLimit');
                    }
                    if (event) {
                      setValue('owner.individual.ageLimit', 0);
                    }
                  }}
                />
                {errorLegalType && errorLegalType.message && (
                  <FormErrorLabel label={errorLegalType.message} />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} columnSpacing={3} sx={{ mt: 1, rowGap: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Grid container sx={{ gap: 1.5, alignItems: 'center', mb: 1 }}>
              <FormLabel
                name="ownerLegalEntityOrganisationData"
                label={t('licence.legalEntityOrganisationData.label')}
                disabled={!organizationUserType}
              />
              {organizationUserType && (
                <StyledLink
                  onClick={() => {
                    checkAll('ownerLegalEntityOrganisation');
                  }}
                >
                  {allCheckedOrganisation
                    ? t('global.unselect.label')
                    : t('global.selectAll.label')}
                </StyledLink>
              )}
            </Grid>
            {ownerLegalEntityOrganisationDataFields
              .filter((val: string) => !hiddenFieldsName.includes(val as EFieldsOptional))
              .map((item: string) => (
                <FormCheckbox
                  name={`owner.organisation.${item}`}
                  label={t(
                    ELicenceCardInformationFormLabels?.[
                      item as keyof typeof ELicenceCardInformationFormLabels
                    ],
                  )}
                  control={control}
                  errors={errors}
                  withValidation={false}
                  onChange={(e) => enableLegalEntityOrganisationData(e, item)}
                  disabled={
                    !organizationUserType ||
                    ownerLegalEntityOrganisationDisable.includes(item) ||
                    requiredFieldsName.includes(item as EFieldsOptional)
                  }
                />
              ))}
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Grid container sx={{ gap: 1.5, alignItems: 'center', mb: 1 }}>
              <FormLabel
                name="ownerLegalEntityIndividualData"
                label={t('licence.legalEntityIndividualData.label')}
                disabled={!individualUserType}
              />
              {individualUserType && (
                <StyledLink
                  onClick={() => {
                    checkAll('ownerLegalEntityIndividual');
                  }}
                >
                  {allCheckedIndividual
                    ? t('global.unselect.label')
                    : t('global.selectAll.label')}
                </StyledLink>
              )}
            </Grid>
            {ownerLegalEntityIndividualDataFields
              .filter((val: string) => !hiddenFieldsName.includes(val as EFieldsOptional))
              .map((item: string) => (
                <FormCheckbox
                  name={`owner.individual.${item}`}
                  label={t(
                    ELicenceCardInformationFormLabels?.[
                      item as keyof typeof ELicenceCardInformationFormLabels
                    ],
                  )}
                  onChange={(e) => enableLegalEntityIndividualData(e, item)}
                  control={control}
                  errors={errors}
                  withValidation={false}
                  disabled={
                    !individualUserType ||
                    ownerLegalEntityIndividualDisable.includes(item) ||
                    requiredFieldsName.includes(item as EFieldsOptional)
                  }
                />
              ))}
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormNumberInput
              name="owner.individual.ageLimit"
              label={t(ELicenceFormLabels.ageLimit)}
              control={control}
              errors={errors}
              disabled={!individualUserType}
              required={individualUserType}
            />
          </Grid>
        </Grid>

        <Grid container size={{ xs: 12 }}>
          <StyledNameSection>
            {t('licenceConfiguration.add.licenceConditions.label')}
          </StyledNameSection>
        </Grid>
        <Grid sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: colors.gray900 }}>
            {t('licenceConfiguration.add.licenceConditions.description')}
          </Typography>
        </Grid>
        <Grid container columnSpacing={3} sx={{ mt: 1, rowGap: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.specificCountryArea"
              label={t(ELicenceFormLabels.specificCountryArea)}
              options={[
                { label: t('licence.areaRestriction.national'), value: 'true' },
                { label: t('licence.areaRestriction.specificArea'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.competency"
              label={t(ELicenceFormLabels.competency)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.safeStorage"
              label={t(ELicenceFormLabels.safeStorage)}
              options={[
                { label: t('general.no.label'), value: 'false' },
                {
                  label: t(ELicenceFormLabels.safeStorageText),
                  value: EsafeStorageValues.Text,
                },
                {
                  label: t(ELicenceFormLabels.safeStorageObject),
                  value: EsafeStorageValues.Object,
                },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.weaponCategory"
              label={t(ELicenceFormLabels.weaponCategory)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.weaponNumberLimit"
              label={t(ELicenceFormLabels.weaponNumberLimit)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.carryConditions"
              label={t(ELicenceFormLabels.carryConditions)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.additionalConditions"
              label={t(ELicenceFormLabels.additionalConditions)}
              options={[
                { label: t('general.yes.label'), value: 'true' },
                { label: t('general.no.label'), value: 'false' },
              ]}
              control={control}
              errors={errors}
              onChangeOption={conditionChangeValue}
              booleanType
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormRadioGroup
              name="conditions.goodReason"
              label={t(ELicenceFormLabels.goodReason)}
              options={[
                { label: t('general.no.label'), value: 'false' },
                { label: t(ELicenceFormLabels.goodReasonText), value: EgoodReasonValues.Text },
                {
                  label: t(ELicenceFormLabels.goodReasonDictionary),
                  value: EgoodReasonValues.Dictionary,
                },
              ]}
              control={control}
              errors={errors}
              onChangeOption={() => {
                disabledGoodReasonDictionary();
              }}
            />
          </Grid>
          {isGoodReasonDictionary && (
            <FormListWrapper
              isError={!!errors.conditions?.goodReasonDictionaryIds}
              errorMessage={t('general.field.validation.required')}
            >
              <Grid size={{ xs: 12 }}>
                <Grid container sx={{ gap: 1, alignItems: 'center', mb: 1 }}>
                  <FormLabel
                    name="goodReasonDictionaryIds"
                    label={t('licence.goodReasonDictionaryIds.label')}
                  />
                  <StyledLink onClick={() => checkAll('goodReasonDictionary')}>
                    {allCheckedGoodReasonDictionaryIds
                      ? t('global.unselect.label')
                      : t('global.selectAll.label')}
                  </StyledLink>
                </Grid>
                <Grid container size={{ xs: 12 }} sx={{ rowGap: 1 }}>
                  {weaponLicenceGoodReason &&
                    weaponLicenceGoodReason.map((item: IDictionary) => (
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormCheckbox
                          name={`conditions.goodReasonDictionaryIds.${item.uniqueId}`}
                          label={item.name}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          booleanType
                        />
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            </FormListWrapper>
          )}
        </Grid>

        {/* <Grid container size={{ xs: 12 }}>
          <StyledNameSection>
            {t('licenceConfiguration.add.licenceDocumentInformation.label')}
          </StyledNameSection>
        </Grid>
        <Grid container size={{ xs: 12 }} style={{ marginBottom: '16px' }}>
          <Typography variant="body2" sx={{ color: colors.gray900 }}>
            {t('licenceConfiguration.add.licenceDocumentInformation.description')}
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 12,  sm: 4, lg: 4 }}>
            <Grid container size={{ xs: 12 }} gap={1.5} alignItems="center">
              <FormLabel name="licenceData" label={t('licence.licenceData.label')} />
              <StyledLink
                onClick={() => {
                  checkAll('licenceData');
                }}
              >
                {t('global.selectAll.label')}
              </StyledLink>
            </Grid>
            {licenceDataFields.map((item: string) => (
              <FormCheckbox
                name={`documentInformation.${item}`}
                label={t(
                  ELicenceCardInformationFormLabels?.[
                    item as keyof typeof ELicenceCardInformationFormLabels
                  ],
                )}
                control={control}
                errors={errors}
                withValidation={false}
                disabled={
                  (organisationRequired.includes(item) && !organizationUserType) ||
                  (individualRequired.includes(item) && !individualUserType) ||
                  checkboxIsDisable(item)
                }
              />
            ))}
          </Grid>
          <Grid size={{ xs: 12,  sm: 4, lg: 4 }}>
            <Grid container size={{ xs: 12 }} gap={1.5} alignItems="center">
              <FormLabel name="weaponData" label={t('licence.weaponData.label')} />
              <StyledLink
                onClick={() => {
                  checkAll('weaponData');
                }}
              >
                {t('global.selectAll.label')}
              </StyledLink>
            </Grid>
            {weaponDataFields.map((item: string) => (
              <FormCheckbox
                name={`documentInformation.${item}`}
                label={t(
                  ELicenceCardInformationFormLabels?.[
                    item as keyof typeof ELicenceCardInformationFormLabels
                  ],
                )}
                control={control}
                errors={errors}
                withValidation={false}
                disabled={
                  (organisationRequired.includes(item) && !organizationUserType) ||
                  (individualRequired.includes(item) && !individualUserType) ||
                  checkboxIsDisable(item)
                }
              />
            ))}
            <Grid container size={{ xs: 12 }} gap={1.5} mt={2.5} alignItems="center">
              <FormLabel
                name="legalEntityOrganisationData"
                label={t('licence.legalEntityOrganisationData.label')}
              />
              <StyledLink
                onClick={() => {
                  checkAll('legalEntityOrganisationData');
                }}
              >
                {t('global.selectAll.label')}
              </StyledLink>
            </Grid>
            {documentLegalEntityOrganisationDataFields.map((item: string) => (
              <FormCheckbox
                name={`documentInformation.${item}`}
                label={t(
                  ELicenceCardInformationFormLabels?.[
                    item as keyof typeof ELicenceCardInformationFormLabels
                  ],
                )}
                control={control}
                errors={errors}
                withValidation={false}
                disabled={checkDocumentLegalEntity(item, ELegalEntityTypes.organisation)}
              />
            ))}
          </Grid>
          <Grid size={{ xs: 12,  sm: 4 }}>
            <Grid container size={{ xs: 12 }} gap={1.5} alignItems="center">
              <FormLabel
                name="legalEntityIndividualData"
                label={t('licence.legalEntityIndividualData.label')}
              />
              <StyledLink
                onClick={() => {
                  checkAll('legalEntityIndividualData');
                }}
              >
                {t('global.selectAll.label')}
              </StyledLink>
            </Grid>
            {documentLegalEntityIndividualDataFields.map((item: string) => (
              <FormCheckbox
                name={`documentInformation.${item}`}
                label={t(
                  ELicenceCardInformationFormLabels?.[
                    item as keyof typeof ELicenceCardInformationFormLabels
                  ],
                )}
                control={control}
                errors={errors}
                withValidation={false}
                disabled={checkDocumentLegalEntity(item, ELegalEntityTypes.individual)}
              />
            ))}
          </Grid>
        </Grid> */}

        <Grid container columnSpacing={3} sx={{ rowGap: { xs: 3, sm: 2 }, mt: 4 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Button
              type="submit"
              fullWidth
              label={t('general.save.button')}
              id="save-button"
              variant={EButtonVariants.contained}
              isLoading={isLoading}
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel-button"
              variant={EButtonVariants.outlined}
              onClick={() =>
                navigate(
                  isEdit && id
                    ? ERouteLinks.LicenceTemplatePage.replace(':id', id)
                    : ERouteLinks.LicenceConfigurationPage,
                )
              }
              disabled={isLoading}
            />
          </Grid>
        </Grid>

        <Grid sx={{ mt: 2 }}>
          <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
        </Grid>
      </div>
    </form>
  );
};

export { FormLicenceTemplatePage };
